import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from './ui/Button';
const tiers = [{
  name: 'Starter',
  price: {
    monthly: 0,
    annual: 0
  },
  description: 'Perfect for hobbyists and students.',
  features: ['5 mockups per month', 'Standard resolution', 'Public projects', 'Community support'],
  cta: 'Start Free',
  highlight: false
}, {
  name: 'Pro',
  price: {
    monthly: 29,
    annual: 24
  },
  description: 'For professional designers and freelancers.',
  features: ['Unlimited mockups', '4K resolution', 'Private projects', 'Figma plugin', 'Priority support'],
  cta: 'Get Pro',
  highlight: true
}, {
  name: 'Team',
  price: {
    monthly: 79,
    annual: 65
  },
  description: 'Collaborative features for growing teams.',
  features: ['Everything in Pro', '5 team members', 'Shared library', 'API access', 'SSO authentication'],
  cta: 'Contact Sales',
  highlight: false
}];
export function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  return <section id="pricing" className="py-32 px-6 bg-[#0a0a0a]" data-id="element-160">
      <div className="max-w-7xl mx-auto" data-id="element-161">
        <div className="text-center mb-16" data-id="element-162">
          <h2 className="text-4xl md:text-6xl font-bold mb-6" data-id="element-163">
            Simple pricing
          </h2>
          <p className="text-[#a3a3a3] mb-8" data-id="element-164">
            Start for free, upgrade when you need more power.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-4" data-id="element-165">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-[#666]'}`} data-id="element-166">
              Monthly
            </span>
            <button onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')} className="w-14 h-8 bg-[#222] rounded-full p-1 relative transition-colors hover:bg-[#333]" data-id="element-167">
              <motion.div animate={{
              x: billingCycle === 'monthly' ? 0 : 24
            }} className="w-6 h-6 bg-[#ff6b35] rounded-full shadow-lg" data-id="element-168" />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-white' : 'text-[#666]'}`} data-id="element-169">
              Annual <span className="text-[#ff6b35] text-xs ml-1" data-id="element-170">-20%</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start" data-id="element-171">
          {tiers.map((tier, index) => <motion.div key={tier.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className={`relative p-8 rounded-2xl border ${tier.highlight ? 'border-[#ff6b35] bg-[#ff6b35]/5' : 'border-[#222] bg-[#0f0f0f]'}`} data-id="element-172">
              {tier.highlight && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#ff6b35] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-173">
                  Most Popular
                </div>}

              <h3 className="text-xl font-bold mb-2" data-id="element-174">{tier.name}</h3>
              <div className="flex items-baseline gap-1 mb-4" data-id="element-175">
                <span className="text-4xl font-bold font-['Syne']" data-id="element-176">
                  $
                  {billingCycle === 'monthly' ? tier.price.monthly : tier.price.annual}
                </span>
                <span className="text-[#666]" data-id="element-177">/mo</span>
              </div>
              <p className="text-[#a3a3a3] text-sm mb-8 h-10" data-id="element-178">
                {tier.description}
              </p>

              <ul className="space-y-4 mb-8" data-id="element-179">
                {tier.features.map(feature => <li key={feature} className="flex items-start gap-3 text-sm text-[#f5f5f5]" data-id="element-180">
                    <Check className="w-5 h-5 text-[#ff6b35] shrink-0" data-id="element-181" />
                    {feature}
                  </li>)}
              </ul>

              <Button variant={tier.highlight ? 'primary' : 'ghost'} className="w-full" data-id="element-182">
                {tier.cta}
              </Button>
            </motion.div>)}
        </div>
      </div>
    </section>;
}